import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import {
  ContentPermissionsFragment,
  useContentPermissionsQuery,
} from "@src/features/libraryItem/hooks/useContentPermissions.generated";
import { draftVersionSelectedAtom, moduleVersionModuleIdAtom } from "../atoms";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { isEqual } from "lodash";
import { selectAtom } from "jotai/utils";
import { ALL_FALSE_CONTENT_PERMISSIONS } from "../../hooks/useContentPermissions";
import { useActiveLibraryItemMainTab } from "@src/features/libraryItem/contexts/libraryItemMainTabStore";
import useIsCatalogLibraryItemPage from "@src/features/libraryItem/hooks/useIsCatalogLibraryItemPage";

type Options = {
  /**
   * Whether the module's read-only "preview" tab zeroes edit permission (the
   * default). The tab context turns this off: it needs the version-only
   * answer to decide whether Build is offered at all — asking the tab-masked
   * one from the preview tab would say "cannot edit" and refuse the switch.
   */
  maskViewingTab?: boolean;
};

const useModuleBuilderContentPermission = ({
  maskViewingTab = true,
}: Options = {}): ContentPermissionsFragment | undefined => {
  // const moduleId = 564332;
  const { userActionPermissions } = useActionPermissions();
  const draftVersionSelected = useAtomValue(draftVersionSelectedAtom);
  const input = useAtomValue(
    useMemo(
      () =>
        selectAtom(
          moduleVersionModuleIdAtom,
          (moduleId) => ({
            moduleId,
          }),
          isEqual,
        ),
      [],
    ),
  );
  const { data } = useContentPermissionsQuery({
    variables: input,
    skip: !input.moduleId,
    fetchPolicy: "cache-first",
  });
  const permissions = data?.ContentPermissions;
  // The "preview" tab is the read-only view of the draft: the canvas shows
  // the same version as Build, so the version alone cannot say whether it may
  // be edited — the tab does. Publishing stays a matter of the version only.
  const catalogPage = useIsCatalogLibraryItemPage();
  const activeMainTab = useActiveLibraryItemMainTab();
  const viewingTab = maskViewingTab && activeMainTab === "preview";
  const hasEditContentPermission =
    (permissions?.hasEditContentPermission &&
      draftVersionSelected &&
      !viewingTab) ||
    false;
  const result = useMemo(
    () => ({
      hasEditAdminAndManagerSharingPermission:
        permissions?.hasEditAdminAndManagerSharingPermission || false,
      hasShareWithIndividualsPermission:
        permissions?.hasShareWithIndividualsPermission || false,
      hasEditContentPermission,
      hasPublishPermission:
        (permissions?.hasPublishPermission && draftVersionSelected) || false,
      hasCommentPermission:
        !catalogPage &&
        ((permissions?.hasCommentPermission && draftVersionSelected) ||
          hasEditContentPermission ||
          false),
    }),
    [catalogPage, draftVersionSelected, hasEditContentPermission, permissions],
  );

  if (!data?.ContentPermissions) return undefined;
  if (!userActionPermissions?.createContent) {
    // Preserve ability to comment while blocking edit/publish/sharing when the
    // user lacks org-level createContent. This allows individual collaborators to comment.
    return {
      ...ALL_FALSE_CONTENT_PERMISSIONS,
      hasEditContentPermission: result.hasEditContentPermission,
      hasCommentPermission: result.hasCommentPermission,
    };
  }
  return result;
};

export default useModuleBuilderContentPermission;
